import React, { useState, useEffect } from 'react';
import { svgCertificateGenerator } from '../../lib/svgCertificateGenerator';
import { getServerSessionId } from '../../lib/serverSession';
import { sanitizeSvg } from '../../lib/sanitizeHtml';

interface LiveCertificateEditorProps {
  certificateNumber: string;
  sharesCount: number;
  purchaseId: string;
  userId: number;
  issueDate?: string | null;
  onAddressChange?: (address: { line1: string; line2: string; city: string; country: string; postalCode: string }) => void;
  refreshTrigger?: number;
}

export const LiveCertificateEditor: React.FC<LiveCertificateEditorProps> = ({
  certificateNumber,
  sharesCount,
  purchaseId,
  userId,
  issueDate,
  onAddressChange,
  refreshTrigger = 0
}) => {
  const [previewSvg, setPreviewSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(80);

  // Name editing state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Address editing state
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  useEffect(() => {
    loadAddressAndGeneratePreview();
  }, [purchaseId, userId, refreshTrigger]);

  const loadAddressAndGeneratePreview = async () => {
    try {
      setLoading(true);
      setError(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_live_editor_data', data: { purchaseId, userId } })
      });
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      const override = result.override;
      const kyc = result.kyc;

      // Set name fields from override or KYC
      if (override?.first_name || override?.last_name) {
        setFirstName(override.first_name || '');
        setLastName(override.last_name || '');
      } else if (kyc) {
        setFirstName(kyc.first_name || '');
        setLastName(kyc.last_name || '');
      }

      // Set address fields
      if (override?.address_line1 || override?.address_line2) {
        setAddressLine1(override.address_line1 || '');
        setAddressLine2(override.address_line2 || '');
        setCity(override.city || '');
        setCountry(override.country || '');
        setPostalCode(override.postal_code || '');
      } else if (kyc) {
        setAddressLine1(kyc.street_address || '');
        setAddressLine2(kyc.province || '');
        setCity(kyc.city || '');
        setCountry(kyc.country_name || '');
        setPostalCode(kyc.postal_code || '');
      }

      // Generate live SVG preview
      await generateLivePreview();
    } catch (err) {
      console.error('Error loading address:', err);
      setError('Failed to load address data');
    } finally {
      setLoading(false);
    }
  };

  const generateLivePreview = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get payment reference via API
      const sessionId = getServerSessionId();
      const refResponse = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'get_purchase_payment_ref', data: { purchaseId } })
      });
      const refResult = await refResponse.json();

      const paymentReference = refResult.success ? refResult.paymentReference : null;

      // Generate certificate data
      const certData = await svgCertificateGenerator.generateCertificateData(purchaseId);

      if (!certData) {
        throw new Error('Failed to generate certificate data');
      }

      // Override address with current edits
      certData.userAddress = {
        line1: addressLine1,
        line2: addressLine2,
        city: city,
        country: country,
        postalCode: postalCode
      };

      // Override name if provided
      const displayName = (firstName.trim() && lastName.trim())
        ? `${firstName.trim()} ${lastName.trim()}`
        : certData.userName;

      // Load template if not loaded
      await svgCertificateGenerator.loadTemplate();

      // Generate SVG with current address and name
      const svg = await svgCertificateGenerator.generateTestCertificate({
        userName: displayName,
        userIdNumber: certData.userIdNumber,
        addressLine1: addressLine1,
        addressLine2: addressLine2,
        sharesCount: sharesCount,
        certificateNumber: certificateNumber,
        sunId: certData.sunId,
        refNumber: paymentReference || certificateNumber, // Use payment reference or fallback to certificate number
        issueDate: issueDate || certData.issueDate
      } as any);

      setPreviewSvg(svg);
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOverrides = async () => {
    try {
      setSaving(true);
      setSaveStatus(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_live_editor_overrides',
          data: {
            purchaseId,
            userId,
            firstName: firstName.trim() || null,
            lastName: lastName.trim() || null,
            addressLine1: addressLine1.trim() || null,
            addressLine2: addressLine2.trim() || null,
            city: city.trim() || null,
            country: country.trim() || null,
            postalCode: postalCode.trim() || null
          }
        })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error saving overrides:', result.error);
        setSaveStatus('❌ Failed to save changes');
        return;
      }

      setSaveStatus('✅ Changes saved successfully');

      // Notify parent component
      if (onAddressChange) {
        onAddressChange({
          line1: addressLine1,
          line2: addressLine2,
          city: city,
          country: country,
          postalCode: postalCode
        });
      }

      // Regenerate preview with new data
      await generateLivePreview();

      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Error saving overrides:', err);
      setSaveStatus('❌ Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  // Regenerate preview when fields change
  useEffect(() => {
    if (firstName || lastName || addressLine1 || addressLine2) {
      const debounce = setTimeout(() => {
        generateLivePreview();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(debounce);
    }
  }, [firstName, lastName, addressLine1, addressLine2, city, country, postalCode]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Panel: Name & Address Editor */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h4 className="text-white font-semibold mb-4">Certificate Details Override</h4>
        <p className="text-sm text-white mb-4">
          Override the name and address displayed on this user's certificate.
        </p>

        <div className="space-y-4">
          {/* Name Fields */}
          <div className="pb-3 border-b border-gray-700">
            <label className="block text-sm font-medium text-yellow-400 mb-2">👤 Name Override</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-white mb-1">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm text-white mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  placeholder="Last name"
                />
              </div>
            </div>
          </div>

          {/* Address Fields */}
          <div>
            <label className="block text-sm font-medium text-yellow-400 mb-2">📍 Address Override</label>
          </div>

          <div>
            <label className="block text-sm text-white mb-1">Address Line 1</label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
              placeholder="Street address"
            />
          </div>

          <div>
            <label className="block text-sm text-white mb-1">Address Line 2 (Province/State)</label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
              placeholder="Westria, Suriname, N/A"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-white mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="Westria"
              />
            </div>

            <div>
              <label className="block text-sm text-white mb-1">Country</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="Suriname"
              />
            </div>

            <div>
              <label className="block text-sm text-white mb-1">Postal Code</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="N/A"
              />
            </div>
          </div>

          <div className="pt-3 border-t border-gray-700">
            <button
              onClick={handleSaveOverrides}
              disabled={saving}
              className={`w-full px-4 py-2 rounded font-semibold transition-colors ${
                saving
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-yellow-500 hover:bg-yellow-400 text-black'
              }`}
            >
              {saving ? 'Saving...' : '💾 Save Name & Address'}
            </button>
            {saveStatus && (
              <p className="text-sm mt-2 text-center">{saveStatus}</p>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel: Live Preview */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-semibold">Live Certificate Preview</h4>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoom(Math.max(50, zoom - 10))}
              className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded"
            >
              -
            </button>
            <span className="text-sm text-white">{zoom}%</span>
            <button
              onClick={() => setZoom(Math.min(150, zoom + 10))}
              className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded"
            >
              +
            </button>
            <button
              onClick={() => setZoom(80)}
              className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded"
            >
              Reset
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400"></div>
            <span className="ml-3 text-gray-300 text-sm">Generating preview...</span>
          </div>
        ) : error ? (
          <div className="text-red-400 text-sm p-4 bg-red-900/20 rounded">
            {error}
          </div>
        ) : previewSvg ? (
          <div className="bg-white rounded border border-gray-300 overflow-auto" style={{ maxHeight: '600px' }}>
            <div
              style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
              dangerouslySetInnerHTML={{ __html: sanitizeSvg(previewSvg) }}
            />
          </div>
        ) : (
          <div className="text-white text-sm text-center py-12">
            No preview available
          </div>
        )}
      </div>
    </div>
  );
};

